import React from 'react';
import styled, { css } from 'styled-components';
import { Box, ChoiceBoxProps } from '@sparknz/spark-pattern-library';
import { trackCustomLink } from '@sparknz/tracking';

import { InputOption } from 'journey/productDetails/typings';

interface VisualStateProps {
  checked: boolean;
  disabled?: boolean;
}

const ColorSelectorInput = styled.input`
  appearance: none;
  margin: 0;
  background-color: transparent;
  position: absolute;
  opacity: 0;
  z-index: ${({ theme: { zIndex } }) => zIndex.negative};
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const Circle = styled.div<{ color: string }>`
  ${({
    theme: {
      borderRadius,
      palette: { baseColors },
    },
    color,
  }) => css`
    background-color: ${color};
    height: 23px;
    width: 23px;
    border-radius: ${borderRadius.round};
    box-shadow: 0 0 0 1px
      ${color.toUpperCase() === '#FFFFFF' ? baseColors.alto : color};
  `}
`;

const RadioButtonWrapper = styled.label<VisualStateProps>`
  ${({ checked, disabled, theme: { palette, borderRadius, spacing } }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-around;
    height: 33px;
    width: 33px;
    border: 2px solid
      ${checked ? palette.baseColors.lupin : palette.baseColors.wairua};
    ${disabled && `border-color: ${palette.baseColors.alto}`};
    border-radius: ${borderRadius.round};
    transition: border-color 0.2s ease, background-color 0.2s ease;
    margin-right: ${spacing.s2};
    cursor: pointer;
  `}
`;

const ColorSelectorContainer = styled(Box)`
  ${({ theme: { media, spacing } }) => css`
    justify-content: center;

    & > :last-child {
      margin-right: 0;
    }

    ${media.up('slim')} {
      margin-bottom: ${spacing.s4};
      justify-content: left;
    }

    ${media.up('big')} {
      margin-bottom: ${spacing.s5};
    }
  `}
`;

interface ColorChoiceBoxProps<
  Props = React.InputHTMLAttributes<HTMLInputElement>,
> extends ChoiceBoxProps {
  inputProps?: Props;
}

const ColorChoiceBox = React.forwardRef(
  (props: ColorChoiceBoxProps, ref: React.Ref<HTMLInputElement>) => {
    const { checked, onChange, value, name, inputProps } = props;
    return (
      <RadioButtonWrapper
        title={name}
        checked={checked}
        onClick={() => {
          trackCustomLink(name);
        }}
      >
        <Circle color={value} />
        <ColorSelectorInput
          type="radio"
          checked={checked}
          onChange={onChange}
          name={name}
          ref={ref}
          {...inputProps}
        />
      </RadioButtonWrapper>
    );
  },
);

interface ColorSelectorProps {
  options: InputOption[];
  onChange: (colorName: string, colorHex: string) => void;
  selectedValue: string;
}

const ColorSelector: React.FC<ColorSelectorProps> = ({
  options,
  onChange,
  selectedValue,
}) => {
  return (
    <ColorSelectorContainer flexDirection="row">
      {options?.map(({ label, value }) => {
        const checked = selectedValue === value;
        return (
          <ColorChoiceBox
            key={label}
            checked={checked}
            name={label}
            value={value}
            onChange={() => onChange(label, value)}
            inputProps={{ 'aria-label': label, 'aria-checked': checked }}
          />
        );
      })}
    </ColorSelectorContainer>
  );
};

export default ColorSelector;
